import React from 'react';
import { Avatar, Box, Chip, ListItemButton, Typography } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { formatCurrency } from '../../../shared/utils/currency';
import type { AdminDeductionRow } from '@billda/api';

const stringToColor = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 45%, 55%)`;
};

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? '';
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const buildSubheading = (deduction: AdminDeductionRow): string => {
  const parts: string[] = [];
  parts.push(deduction.siteName ?? 'No site');
  parts.push(deduction.plotReference ?? 'No plot');
  return parts.join(' · ');
};

const formatBookedDate = (dateString: string): string =>
  format(parseISO(dateString), 'dd MMM yyyy');

const statusChipConfig: Record<
  string,
  { color: 'warning' | 'error' | 'default'; label: string } | undefined
> = {
  Booked: { color: 'warning', label: 'Booked' },
  Rejected: { color: 'error', label: 'Rejected' },
  Cancelled: { color: 'default', label: 'Cancelled' },
};

export interface DeductionListItemProps {
  deduction: AdminDeductionRow;
  onClick?: (deduction: AdminDeductionRow) => void;
  'data-testid'?: string;
}

/**
 * DeductionListItem — memoised list item rendering a single deduction row
 * with creator avatar, description, site/plot subheading, booked-by info
 * on larger screens, and the deduction amount. Accepts `data-testid` for E2E targeting.
 */
export const DeductionListItem = React.memo(function DeductionListItem({
  deduction,
  onClick,
  'data-testid': dataTestId,
}: DeductionListItemProps) {
  const subheading = buildSubheading(deduction);
  const isCancelled = deduction.status === 'Cancelled';
  const chipConfig = statusChipConfig[deduction.status];

  return (
    <ListItemButton
      data-testid={dataTestId}
      onClick={() => onClick?.(deduction)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1.5,
        px: { xs: 2, sm: 3 },
        borderBottom: '1px solid',
        borderColor: 'divider',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'background-color 150ms ease',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      {/* Avatar — creator initials */}
      <Avatar
        sx={{
          width: 32,
          height: 32,
          fontSize: '0.75rem',
          fontWeight: 600,
          bgcolor: stringToColor(deduction.createdByName),
          flexShrink: 0,
        }}
      >
        {getInitials(deduction.createdByName)}
      </Avatar>

      {/* Description + site/plot */}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {deduction.description}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block',
          }}
        >
          {subheading}
        </Typography>
      </Box>

      {/* Status chip — shown for non-Approved statuses */}
      {chipConfig && (
        <Chip
          label={chipConfig.label}
          color={chipConfig.color}
          size="small"
          sx={{ fontWeight: 600, flexShrink: 0 }}
        />
      )}

      {/* Booked by + date — hidden on xs */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: { xs: 'none', md: 'block' },
          flexShrink: 0,
          whiteSpace: 'nowrap',
        }}
      >
        {deduction.createdByName} · {formatBookedDate(deduction.createdAt)}
      </Typography>

      {/* Amount — always visible */}
      <Typography
        variant="body2"
        fontWeight={600}
        sx={{
          width: 80,
          textAlign: 'right',
          flexShrink: 0,
          color: isCancelled ? 'text.disabled' : 'error.main',
          textDecoration: isCancelled ? 'line-through' : 'none',
        }}
      >
        {formatCurrency(deduction.amount)}
      </Typography>
    </ListItemButton>
  );
});
